import { sendUsBrowserRequest, usBrowserSession } from './browserTradingClient';
import { captureUsTime, usRequestStamp, type UsTimeEvidence } from './timeEvidence';
import { decimalUnits } from './ticketModel';
import type { UsClientSession } from './tradingClient';
import type { UsDraft } from './types';

/** Inspection evidence cannot be assigned to the retail executable-preview type. */
export interface InstitutionalDraftInspection {
  kind: 'institutional_draft_inspection';
  accountId: string;
  mappingState: 'structured_evidence' | 'candidate_unconfirmed';
  observedAtMs: number;
  expiresAtMs: number;
  timeEvidence?: UsTimeEvidence;
}
export type InspectInstitutionalDraft = (accountId: string, draft: UsDraft, signal: AbortSignal) => Promise<InstitutionalDraftInspection>;
const invalid = (): never => { throw new Error('Draft check unavailable'); };
const object = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : invalid();
const time = (v: unknown): number => typeof v === 'number' && Number.isSafeInteger(v) && v > 0 && v < 8.64e15 ? v : invalid();
const sameDecimal = (a: unknown, b: string) => typeof a === 'string' && decimalUnits(a) !== null && decimalUnits(a) === decimalUnits(b);

export function decodeInstitutionalDraftInspection(raw: unknown, accountId: string, draft: UsDraft): InstitutionalDraftInspection {
  const r = object(raw), p = object(r.preview), target = object(p.target);
  if (r.venue !== 'polymarket_us' || r.protocol !== 'institutional' || r.canSubmit !== false || r.accountId !== accountId
    || (r.mappingState !== 'structured_evidence' && r.mappingState !== 'candidate_unconfirmed')
    || p.mappingState !== r.mappingState || p.inspectionOnly !== true || p.feeReserveUsd !== null || p.estimatedFeeUsd !== null
    || typeof p.id !== 'string' || !/^[0-9a-f-]{36}$/.test(p.id)
    || p.scopeKey !== draft.scope.scopeKey || p.scopeRevision !== draft.scope.revision || p.draftRevision !== draft.draftRevision
    || target.marketSlug !== draft.target.marketSlug || target.outcome !== draft.target.outcome || p.action !== draft.action
    || !sameDecimal(p.quantity, draft.quantity) || !sameDecimal(p.outcomePrice, draft.outcomePrice)
    || p.timeInForce !== draft.timeInForce || p.postOnly !== draft.postOnly || p.goodTillTime !== (draft.goodTillTime ?? null)) return invalid();
  const observedAtMs = time(r.observedAtMs), expiresAtMs = time(p.expiresAtMs);
  if (expiresAtMs <= observedAtMs || expiresAtMs - observedAtMs > 60_000) return invalid();
  // IDs and provider fields are deliberately not retained as executable state.
  return { kind: 'institutional_draft_inspection', accountId, mappingState: r.mappingState, observedAtMs, expiresAtMs };
}

export function createInstitutionalDraftInspector(deps: {
  session(): UsClientSession | null;
  send(path: string, init: RequestInit, session: UsClientSession): Promise<Response>;
  clock?: () => number;
} = { session: usBrowserSession, send: sendUsBrowserRequest }): InspectInstitutionalDraft {
  const clock = deps.clock ?? Date.now;
  return async (accountId, input, signal) => {
    const session = deps.session();
    if (!session || signal.aborted || !/^[0-9a-f-]{36}$/.test(accountId)) return invalid();
    const draft: UsDraft = { ...input, scope: { ...input.scope }, target: { ...input.target } };
    const current = () => {
      const s = deps.session();
      if (signal.aborted || !s || s.userId !== session.userId || s.orgId !== session.orgId || s.generation !== session.generation) return invalid();
    };
    const started = usRequestStamp(clock);
    const response = await deps.send(`/api/polymarket-us/accounts/${accountId}/previews`, {
      method: 'POST', signal, cache: 'no-store', headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ draft }),
    }, session);
    try { current(); } catch (error) { void response.body?.cancel(); throw error; }
    if (response.status !== 201) { void response.body?.cancel(); return invalid(); }
    const reader = response.body?.getReader();
    if (!reader) return invalid();
    let text = '', bytes = 0;
    const decoder = new TextDecoder();
    try {
      for (;;) {
        const chunk = await reader.read(); current();
        if (chunk.done) break;
        bytes += chunk.value.byteLength;
        if (bytes > 16_384) return invalid();
        text += decoder.decode(chunk.value, { stream: true });
      }
      text += decoder.decode(); current();
      const result = decodeInstitutionalDraftInspection(JSON.parse(text), accountId, draft);
      return { ...result, timeEvidence: captureUsTime(result.observedAtMs, started, clock) };
    } finally { void reader.cancel().catch(() => {}); }
  };
}
